import type {
  SubEntryType,
  SubEntry,
  DeepPartial,
} from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActivityType,
  DeviceType,
  ExerciseType,
  ExerciseIntensity,
} from "@tandemdiabetes/sugarmate-api-react-client"
import type {
  EditableSubEntry,
  EditableUserEntry,
} from "src/types"

// this type is like EditableSubEntry but it isn't read-only
type PartialSubEntry<T extends SubEntryType | undefined> = DeepPartial<SubEntry> & {
  subentry_type: T;
}

// merges the currentSubEntry into currentEntry
export function mergeEntryWithSubEntry(
  currentEntry: EditableUserEntry,
  currentSubEntry?: EditableSubEntry,
) {
  if (currentSubEntry) {
    const newSubEntry = {
      ...currentSubEntry,
      note: currentSubEntry.note || null, // replace empty string with null
    }

    /*
     * num_units is required by the backend but not by our form
     *
     * replace empty num_units with 0
     */
    if (currentSubEntry.subentry_type === ActivityType.Insulin) {
      const insulinSubEntry =
        newSubEntry as PartialSubEntry<ActivityType.Insulin>
      insulinSubEntry.insulin = {
        num_units: 0,
        ...(insulinSubEntry.insulin || {}),
      }
    }

    /*
     * num_units is required by the backend but not by our form
     *
     * replace empty num_units with 0
     */
    if (currentSubEntry.subentry_type === ActivityType.Medication) {
      const insulinSubEntry =
        newSubEntry as PartialSubEntry<ActivityType.Medication>
      insulinSubEntry.med = {
        num_units: 0,
        ...(insulinSubEntry.med || {}),
      }
    }

    /*
     * device_type is required by the backend but not by our form
     *
     * replace empty device type with DeviceType.NotProvided
     */
    if (currentSubEntry.subentry_type === ActivityType.DeviceChange) {
      const deviceChangeSubEntry =
        newSubEntry as PartialSubEntry<ActivityType.DeviceChange>
      deviceChangeSubEntry.device_change = {
        device_type: DeviceType.NotProvided,
        ...(deviceChangeSubEntry.device_change || {}),
      }
    }

    /*
     * exercise_type and exercise_intensity is required by the backend but not
     * by our form
     *
     * replace empty exercise type with ExerciseType.None
     */
    if (currentSubEntry.subentry_type === ActivityType.Exercise) {
      const exerciseSubEntry =
        newSubEntry as PartialSubEntry<ActivityType.Exercise>
      exerciseSubEntry.exercise = {
        exercise_type: ExerciseType.None,
        intensity: ExerciseIntensity.None,
        ...(exerciseSubEntry.exercise || {}),
      }
    }

    // store newSubEntry in currentEntry
    if (newSubEntry._id) {
      const subentries = currentEntry.subentries.map((sub) => {
        if (newSubEntry._id === sub._id) {
          return newSubEntry
        }

        return sub
      })

      return {
        ...currentEntry,
        subentries,
      }
    }

    // add new subEntry to currentEntry
    const subentries = [
      ...currentEntry.subentries,
      newSubEntry,
    ]

    return {
      ...currentEntry,
      subentries,
    }
  }
  return currentEntry
}
